import { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { IndianRupee, CalendarClock, PiggyBank } from "lucide-react";

const TENURES = [
  { years: 1, rate: 0.16 },
  { years: 3, rate: 0.20 },
  { years: 5, rate: 0.24 },
];

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

export default function Calculator() {
  const [amount, setAmount] = useState(100000);
  const [tenure, setTenure] = useState(TENURES[1]);

  const monthly = (amount * tenure.rate) / 12;
  const total = amount + amount * tenure.rate * tenure.years;

  return (
    <section id="calculator" data-testid="investment-calculator-section" className="bg-[#eef2f7] py-24">
      <div className="mx-auto max-w-6xl px-4">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Yield calculator</p>
          <h2 className="font-heading mt-4 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            See what your fraction could earn
          </h2>
          <p className="mt-4 text-base text-slate-600">
            Slide to your comfortable ticket size. Estimates are indicative, based on typical
            franchise outlet performance — not a guaranteed return.
          </p>
        </div>

        <div className="neu-card mx-auto mt-12 grid max-w-4xl gap-10 rounded-[2rem] p-8 sm:p-10 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold text-slate-700">Your investment amount</label>
              <span className="neu-badge rounded-full px-4 py-1.5 font-heading text-base font-black text-orange-600" data-testid="calc-amount-display">
                {inr(amount)}
              </span>
            </div>
            <div className="mt-6 px-1">
              <Slider
                data-testid="calc-amount-slider"
                value={[amount]}
                onValueChange={(v) => setAmount(v[0])}
                min={5000}
                max={1000000}
                step={5000}
              />
              <div className="mt-2 flex justify-between text-[11px] font-semibold text-slate-400">
                <span>₹5,000</span>
                <span>₹10,00,000</span>
              </div>
            </div>

            <label className="mt-8 block text-sm font-bold text-slate-700">Investment tenure</label>
            <div className="mt-3 flex gap-3" data-testid="calc-tenure-selector">
              {TENURES.map((t) => (
                <button
                  key={t.years}
                  data-testid={`calc-tenure-${t.years}y`}
                  onClick={() => setTenure(t)}
                  className={`flex-1 rounded-2xl px-4 py-3 text-sm font-bold transition-all ${
                    tenure.years === t.years
                      ? "clay-btn bg-orange-500 text-white"
                      : "neu-badge text-slate-600 hover:text-orange-600"
                  }`}
                >
                  {t.years} Year{t.years > 1 ? "s" : ""}
                </button>
              ))}
            </div>

            <div className="mt-8 space-y-3">
              {[
                { label: "Store sales pool", w: "100%", cls: "bg-slate-300" },
                { label: "Operator & brand share", w: "62%", cls: "bg-slate-400" },
                { label: "Investor payout share", w: "38%", cls: "bg-orange-500" },
              ].map((b) => (
                <div key={b.label}>
                  <p className="mb-1 text-[11px] font-bold text-slate-500">{b.label}</p>
                  <div className="h-3 overflow-hidden rounded-full bg-white/70">
                    <div className={`h-full rounded-full ${b.cls}`} style={{ width: b.w }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {[
              { icon: IndianRupee, label: "Est. monthly payout", value: inr(monthly), testid: "calc-monthly-payout" },
              { icon: CalendarClock, label: "Annualised yield (indicative)", value: `${Math.round(tenure.rate * 100)}%`, testid: "calc-annual-roi" },
              { icon: PiggyBank, label: `Projected value in ${tenure.years} yr${tenure.years > 1 ? "s" : ""}`, value: inr(total), testid: "calc-total-value" },
            ].map(({ icon: Icon, label, value, testid }) => (
              <div key={label} className="neu-inset flex items-center gap-4 rounded-3xl p-5" data-testid={testid}>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-lg shadow-orange-500/40">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-heading text-2xl font-black text-slate-900">{value}</p>
                  <p className="text-xs font-semibold text-slate-500">{label}</p>
                </div>
              </div>
            ))}
            <a
              href="#lead-form"
              data-testid="calc-lock-allocation-btn"
              className="clay-btn mt-2 rounded-full bg-orange-500 px-6 py-4 text-center text-base font-bold text-white"
            >
              Lock In My Fractional Allocation
            </a>
            <p className="text-center text-[10px] leading-relaxed text-slate-400">
              Indicative illustration only. Actual returns depend on outlet performance and are not guaranteed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
